import { useCallback } from "react";
import {
  STRAVA_OAUTH_URL,
  OAUTH_SCOPES,
  STORAGE_KEYS,
  STATE_EXPIRY_MS,
  getStravaClientId,
  getStravaRedirectUri,
  getStravaExchangeUrl,
} from "../constants";
import {
  StravaOAuthState,
  StravaExchangeResponse,
  StravaExchangeError,
  StravaActivitiesListResponse,
  StravaExchangeActivitiesRequest,
  StravaFetchActivitiesRequest,
} from "../types";

/**
 * Hook for managing the Strava OAuth flow.
 *
 * Handles:
 * - Initiating OAuth with CSRF protection
 * - Validating state on callback
 * - Exchanging authorization code for data via Edge Function
 */
export const useStravaOAuth = () => {
  /**
   * Generate a cryptographically secure state string for CSRF protection.
   */
  const generateState = useCallback((): string => {
    return crypto.randomUUID();
  }, []);

  /**
   * Save OAuth state to localStorage for later validation.
   */
  const saveOAuthState = useCallback((state: string, returnTo: string): void => {
    const oauthState: StravaOAuthState = {
      state,
      createdAt: Date.now(),
      returnTo,
    };
    localStorage.setItem(STORAGE_KEYS.OAUTH_STATE, JSON.stringify(oauthState));
  }, []);

  /**
   * Load OAuth state from localStorage.
   * Returns null if not found or expired.
   */
  const loadOAuthState = useCallback((): StravaOAuthState | null => {
    const stored = localStorage.getItem(STORAGE_KEYS.OAUTH_STATE);
    if (!stored) {
      return null;
    }

    try {
      const state: StravaOAuthState = JSON.parse(stored);

      // Check if state has expired
      if (Date.now() - state.createdAt > STATE_EXPIRY_MS) {
        console.warn("Strava OAuth state has expired");
        localStorage.removeItem(STORAGE_KEYS.OAUTH_STATE);
        return null;
      }

      return state;
    } catch (error) {
      console.error("Failed to parse OAuth state from localStorage:", error);
      localStorage.removeItem(STORAGE_KEYS.OAUTH_STATE);
      return null;
    }
  }, []);

  /**
   * Clear OAuth state from localStorage.
   * Call this after successful OAuth completion.
   */
  const clearOAuthState = useCallback((): void => {
    localStorage.removeItem(STORAGE_KEYS.OAUTH_STATE);
  }, []);

  /**
   * Validate that the received state matches what we stored.
   * This prevents CSRF attacks.
   *
   * @param receivedState - The state parameter from the OAuth callback
   * @returns The stored OAuth state if valid, null if invalid/expired
   */
  const validateState = useCallback(
    (receivedState: string): StravaOAuthState | null => {
      const storedState = loadOAuthState();

      if (!storedState) {
        console.error("No OAuth state found in localStorage");
        return null;
      }

      if (storedState.state !== receivedState) {
        console.error("OAuth state mismatch - possible CSRF attack");
        clearOAuthState();
        return null;
      }

      return storedState;
    },
    [loadOAuthState, clearOAuthState]
  );

  /**
   * Initiate the Strava OAuth flow.
   * Generates state, saves it to localStorage, and redirects to Strava.
   *
   * @param returnTo - URL path to return to after OAuth completes (e.g., "/editor")
   */
  const initiateOAuth = useCallback(
    (returnTo: string = "/"): void => {
      // getStravaClientId throws if env var is missing - let it propagate
      // so callers can handle the error appropriately
      const clientId = getStravaClientId();

      const state = generateState();
      saveOAuthState(state, returnTo);

      const redirectUri = getStravaRedirectUri();

      // Build the authorization URL
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: OAUTH_SCOPES,
        state: state,
      });

      const authUrl = `${STRAVA_OAUTH_URL}?${params.toString()}`;

      // Redirect to Strava
      window.location.href = authUrl;
    },
    [generateState, saveOAuthState]
  );

  /**
   * Exchange the authorization code for Strava data.
   * Calls the strava-exchange Edge Function which handles the token exchange
   * and fetches the user's recent activity.
   *
   * @param code - The authorization code from OAuth callback
   * @returns The exchange response with athlete and activity data
   * @throws Error if the exchange fails
   */
  const exchangeCode = useCallback(
    async (code: string): Promise<StravaExchangeResponse> => {
      const exchangeUrl = getStravaExchangeUrl();
      if (!exchangeUrl) {
        throw new Error("Strava exchange URL not configured");
      }

      const response = await fetch(exchangeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        const errorData: StravaExchangeError = await response.json().catch(() => ({
          error: "Unknown error",
        }));
        throw new Error(errorData.error || `Exchange failed: ${response.status}`);
      }

      const data: StravaExchangeResponse = await response.json();
      return data;
    },
    []
  );

  /**
   * Exchange the authorization code for a list of recent activities.
   * Calls the strava-exchange Edge Function in list mode to get activity summaries
   * without fetching full coordinate streams.
   *
   * @param code - The authorization code from OAuth callback
   * @param after - Optional Unix timestamp to filter activities after this date
   * @param before - Optional Unix timestamp to filter activities before this date
   * @returns The activities list response with athlete and activity summaries
   * @throws Error if the exchange fails
   */
  const exchangeCodeForActivities = useCallback(
    async (
      code: string,
      after?: number,
      before?: number
    ): Promise<StravaActivitiesListResponse> => {
      const exchangeUrl = getStravaExchangeUrl();
      if (!exchangeUrl) {
        throw new Error("Strava exchange URL not configured");
      }

      const body: StravaExchangeActivitiesRequest = {
        code,
        mode: "list",
        fetchActivity: true,
        ...(after !== undefined && { after }),
        ...(before !== undefined && { before }),
      };

      const response = await fetch(exchangeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData: StravaExchangeError = await response.json().catch(() => ({
          error: "Unknown error",
        }));
        throw new Error(errorData.error || `Exchange failed: ${response.status}`);
      }

      const data: StravaActivitiesListResponse = await response.json();
      return data;
    },
    []
  );

  /**
   * Fetch full activity details for a specific activity ID.
   * Requires an access token obtained from the initial OAuth flow.
   * Calls the strava-exchange Edge Function in single mode with activityId.
   *
   * @param activityId - The Strava activity ID to fetch details for
   * @param accessToken - The Strava access token from initial OAuth exchange
   * @returns The exchange response with full activity coordinates
   * @throws Error if the fetch fails
   */
  const fetchActivityDetails = useCallback(
    async (activityId: number, accessToken: string): Promise<StravaExchangeResponse> => {
      const exchangeUrl = getStravaExchangeUrl();
      if (!exchangeUrl) {
        throw new Error("Strava exchange URL not configured");
      }

      if (!accessToken) {
        throw new Error("Access token is required to fetch activity details");
      }

      const response = await fetch(exchangeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ activityId, accessToken }),
      });

      if (!response.ok) {
        const errorData: StravaExchangeError = await response.json().catch(() => ({
          error: "Unknown error",
        }));
        throw new Error(errorData.error || `Fetch failed: ${response.status}`);
      }

      const data: StravaExchangeResponse = await response.json();
      return data;
    },
    []
  );

  /**
   * Fetch activities for a specific date range using a stored access token.
   * This allows refetching activities with different date filters without re-authenticating.
   *
   * @param accessToken - The Strava access token from initial OAuth exchange
   * @param after - Optional Unix timestamp to filter activities after this date
   * @param before - Optional Unix timestamp to filter activities before this date
   * @returns The activities list response with activity summaries
   * @throws Error if the fetch fails
   */
  const fetchActivitiesForDateRange = useCallback(
    async (
      accessToken: string,
      after?: number,
      before?: number
    ): Promise<StravaActivitiesListResponse> => {
      const exchangeUrl = getStravaExchangeUrl();
      if (!exchangeUrl) {
        throw new Error("Strava exchange URL not configured");
      }

      if (!accessToken) {
        throw new Error("Access token is required to fetch activities");
      }

      const body: StravaFetchActivitiesRequest = {
        accessToken,
        mode: "list",
        fetchActivity: true,
        ...(after !== undefined && { after }),
        ...(before !== undefined && { before }),
      };

      const response = await fetch(exchangeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData: StravaExchangeError = await response.json().catch(() => ({
          error: "Unknown error",
        }));
        throw new Error(errorData.error || `Fetch failed: ${response.status}`);
      }

      const data: StravaActivitiesListResponse = await response.json();
      return data;
    },
    []
  );

  return {
    initiateOAuth,
    validateState,
    exchangeCode,
    exchangeCodeForActivities,
    fetchActivityDetails,
    fetchActivitiesForDateRange,
    clearOAuthState,
    loadOAuthState,
  };
};
